/* Mass messages / Priority mass messages pages (sidebar Tools; CONTEXT.md). One
   Account at a time, picked at the top, showing exactly what that Account's own tab
   shows: the same compact Setup + Schedule. The choice lives in ?account= and the
   last one used is remembered per browser, shared by both pages. */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAccounts } from '../context/AccountsContext';
import { useToolAccess } from '../hooks/useToolAccess';
import { AccountPicker, type PickerAccount } from '../components/AccountPicker';
import { PageHeading } from '../components/PageHeader';
import { LoadingBlock } from '../components/Spinner';
import { normalizePlatform } from '../components/PlatformLogo';
import { MassMessagesPage } from '../components/MassMessages/compact/MassMessagesPage';
import type { Kind } from '../components/MassMessages/compact/model';
import { accountName } from '../dashboard/attention';
import { chooseAccount, offeredAccounts, readLastAccount, rememberAccount } from './pickAccount';
// The boxed Account bar, as on Dynamic lists and Tracking links.
import '../dynamicLists/dynamicLists.css';

export function MassMessagesToolPage({ kind }: { kind: Kind }) {
  const { t } = useTranslation('common');
  const tools = useToolAccess();
  const { accounts, loaded } = useAccounts();
  const { getPermissions, loadAccountPermissions } = useAuth();
  const [params, setParams] = useSearchParams();

  const offered = useMemo(() => {
    if (!tools || !loaded) return null;
    const ids = kind === 'priority' ? tools.mass_pages?.priority_mass_messages : tools.mass_pages?.mass_messages;
    return offeredAccounts(accounts, ids)
      .map((a) => ({ account_id: a.account_id, name: accountName(a), username: a.username, platform: a.platform }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tools, loaded, accounts, kind]);

  const fromUrl = params.get('account');
  const accountId = offered ? chooseAccount(offered.map((a) => a.account_id), fromUrl, readLastAccount()) : undefined;
  const account: PickerAccount | undefined = offered?.find((a) => a.account_id === accountId);

  // Keep the URL on the Account shown (a bare open, or one no longer offered).
  useEffect(() => {
    if (!accountId || accountId === fromUrl) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', accountId);
      return next;
    }, { replace: true });
  }, [accountId, fromUrl, setParams]);

  // The compact page reads this Account's permissions; load them on each switch.
  const [permsTried, setPermsTried] = useState<string | null>(null);
  useEffect(() => {
    if (!accountId) return;
    rememberAccount(accountId);
    let live = true;
    void loadAccountPermissions(accountId).finally(() => {
      if (live) setPermsTried(accountId);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAccountPermissions is recreated every render
  }, [accountId]);
  const perms = accountId ? getPermissions(accountId) : undefined;

  const choose = (id: string | undefined) => {
    if (!id) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', id);
      return next;
    });
  };

  const body = () => {
    if (!offered) return <LoadingBlock />;
    if (!account || !accountId) return <p style={{ margin: '24px 0', color: 'var(--om-muted)' }}>{t('massPages.none')}</p>;
    if (!perms) {
      return permsTried === accountId
        ? <p style={{ margin: '24px 0', color: 'var(--om-muted)' }}>{t('massPages.permsFailed')}</p>
        : <LoadingBlock />;
    }
    return <MassMessagesPage key={`${kind}:${accountId}`} kind={kind} accountId={accountId} platform={normalizePlatform(account.platform)} />;
  };

  return (
    <div className="mobile-page-shell cpt-root dl-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading title={t(`massPages.${kind}.title`)} subtitle={t(`massPages.${kind}.subtitle`)} />
      {offered && offered.length > 0 && (
        <div className="dl-bar">
          <div className="dl-bar-account">
            <span className="dl-label">{t('massPages.account')}</span>
            <AccountPicker accounts={offered} value={accountId} onChange={choose} align="left" required />
          </div>
        </div>
      )}
      {body()}
    </div>
  );
}
